import React, { useState, useEffect, useRef } from "react";
import { IonItem, IonLabel, IonInput, IonRow, IonCol } from "@ionic/react";
import { Horse, Event, Person, S3Object } from "../../../models";
import RequiredInputIndicator from "../../Forms/RequiredInputIndicator";
import SelectState from "../../Address/SelectState";
import CogginsDocumentForm from "../../s3Object/CogginsDocumentForm";
import DatePicker from "../../DatePicker/DatePicker";
import { createCogginsDocumentFileName, createS3Object, handleDeleteS3File, handleUploadS3File } from "../../../utilities/s3Object/s3Object";
import { UpdateHorseInput } from "../../../API";
import { getHorseById, updateHorse } from "../../../utilities/horse/Horse";
import { CogginsFileUploadSkeleton } from "../../Skeletons/EntrySectionSkeletons";
import moment from "moment";
import ErrorAlert from "../../Errors/ErrorAlert";
import CogginsWarningBanner from "../../Banners/CogginsWarningBanner";
import {
    CogginsDataFormState,
    CogginsDocumentFormState,
    isCogginsSectionComplete,
} from "../../../utilities/coggins/CogginsSectionValidation";

interface CogginsSectionProps {
    event: Event;
    user: Person;
    selectedHorse?: Horse | null | undefined;
    onSavingStateChange?: (isSaving: boolean) => void;
    onFieldChange?: (areAllRequiredFieldsComplete?: Boolean) => void;
    onRegisterSave?: (fn: () => Promise<void> | void) => void;
}

const CogginsSection: React.FC<CogginsSectionProps> = ({ event, user, selectedHorse, onSavingStateChange, onFieldChange, onRegisterSave }) => {

    // Refs will help us because updating the ref will not cause React to re-render this component.
    // This ref can track the user clicks without necessarily taking any action in the DOM (aka re-rendering)
    const cogginsComponentRef = useRef<any>(null);

    // Basic state
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");
    const [dateWarning, setDateWarning] = useState("");
    const [selectedHorseState, setSelectedHorseState] = useState<Horse | null | undefined>();

    // Coggins Data State
    const [dataFormState, setDataFormState] = useState<CogginsDataFormState>({
        accessionNumber: "",
        date: null,
        state: ""
    });
    
    // Coggins File State
    const [documentFormState, setDocumentFormState] = useState<CogginsDocumentFormState>({
        fileObject: null,
        s3Object: null
    });

    // These refs can persis the most up-to-date value in the event handlers that run outside of React's lifecycle
    // (aka useOnClickOutside)
    const dataRef = useRef(dataFormState);
    const docRef = useRef(documentFormState);

    // keep the data refs in sync:
    useEffect(() => {
        dataRef.current = dataFormState;
    }, [dataFormState]);

    useEffect(() => {
        docRef.current = documentFormState;
    }, [documentFormState]);

    const requiresCogginsData = event.entryOptions?.requiresCogginsData;
    const requiresCogginsDocument = event.entryOptions?.requiresCogginsDocument;
    const cogginsRequirements = { requiresCogginsData, requiresCogginsDocument };

    const reportValidationState = (
        currentDataFormState: CogginsDataFormState,
        currentDocumentFormState: CogginsDocumentFormState
    ) => {
        if (onFieldChange) {
            onFieldChange(isCogginsSectionComplete(currentDataFormState, currentDocumentFormState, cogginsRequirements));
        }
    };

    const handleDataFieldChange = (updatedFormState: CogginsDataFormState) => {
        dataRef.current = updatedFormState;
        reportValidationState(updatedFormState, docRef.current);
    };

    const handleDocumentFieldChange = (updatedFormState: CogginsDocumentFormState) => {
        docRef.current = updatedFormState;
        reportValidationState(dataRef.current, updatedFormState);
    };

    useEffect(() => {
        const loadDataFromSelectedHorse = async (selectedHorse: Horse) => {
            setIsLoading(true);
            setError("");

            // If the horse was already on an EventEntry, then the coggins will not have already been pulled with the horse data
            // If no coggins info exists, double check to make sure it didn't get missed due to the query depth issue
            
            let horseRecord = selectedHorse;
            let selectedHorseCogginsS3Object = selectedHorse.coggins;
            if (!(!!selectedHorseCogginsS3Object)) {
                const queryResult = await getHorseById(selectedHorse.id);
                if (queryResult.isSuccess) {
                    const horseFromDB: Horse = queryResult.result;
                    horseRecord = horseFromDB;
                    setSelectedHorseState(horseFromDB);
                    if (horseFromDB.coggins) selectedHorseCogginsS3Object = horseFromDB.coggins;
                }
            } else {
                setSelectedHorseState(selectedHorse);
            }

            const currentDataFormState: CogginsDataFormState = {
                accessionNumber: horseRecord.cogginsNumber || "",
                date: horseRecord.cogginsDate || null,
                state: horseRecord.cogginsState || ""
            };
            setDataFormState(currentDataFormState);
            dataRef.current = currentDataFormState;
            if (horseRecord.cogginsDate) {
                checkIfDateIsWithinOneYearOfToday(horseRecord.cogginsDate);
            }
            
            // Set the current Document - the Horse will only have the S3Object
            const currentDocumentFormState: CogginsDocumentFormState = {
                fileObject: null,
                s3Object: selectedHorseCogginsS3Object || null
            };
            setDocumentFormState(currentDocumentFormState);
            docRef.current = currentDocumentFormState;
            reportValidationState(currentDataFormState, currentDocumentFormState);
            setIsLoading(false);
        }

        if (selectedHorse) {
           loadDataFromSelectedHorse(selectedHorse);
        } else {
            setSelectedHorseState(null);
            setDateWarning("");
            const currentDataFormState: CogginsDataFormState = {
                accessionNumber: "",
                date: null,
                state: ""
            };
            setDataFormState(currentDataFormState);
            dataRef.current = currentDataFormState;
            const currentDocumentFormState: CogginsDocumentFormState = {
                fileObject: null,
                s3Object: null
            };
            setDocumentFormState(currentDocumentFormState);
            docRef.current = currentDocumentFormState;
            reportValidationState(currentDataFormState, currentDocumentFormState);
        }
    }, [selectedHorse, requiresCogginsData, requiresCogginsDocument]);

    // Register this section's save function with parent
    useEffect(() => {
        if (!onRegisterSave) return;
        const register = () => {
            const fn = async () => {
                const horse = selectedHorseState;
                if (!horse) return;

                const currentData = dataRef.current;
                const currentDoc = docRef.current;

                const hasDataChanges =
                    (currentData.accessionNumber ? currentData.accessionNumber : null) !== (horse.cogginsNumber ? horse.cogginsNumber : null) ||
                    (currentData.date ? currentData.date : null) !== (horse.cogginsDate ? horse.cogginsDate : null) ||
                    (currentData.state ? currentData.state : null) !== (horse.cogginsState ? horse.cogginsState : null);

                const hasDocChanges =
                    currentDoc.fileObject != null ||
                    currentDoc.s3Object?.key !== horse.coggins?.key;

                if (hasDataChanges || hasDocChanges) {
                    await handleSaveHorseData(horse);
                } else {
                    reportValidationState(currentData, currentDoc);
                }
            };
            (onRegisterSave as any)(fn);
        };
        register();
    }, [onRegisterSave, selectedHorseState, requiresCogginsData, requiresCogginsDocument]);

    const handleAccessionNumberChange = (value: string) => {
        const updatedDataFormState: CogginsDataFormState = {
            ...dataFormState,
            accessionNumber: value
        };
        setDataFormState((prev) => ({
            ...prev,
            accessionNumber: value
        }));
        handleDataFieldChange(updatedDataFormState);
    };

    const checkIfDateIsWithinOneYearOfToday = (dateToCheck: (string | null)) => {
        if (!!(dateToCheck)) {
            const today = moment();
            const oneYearAgo = moment().subtract(1, 'year');
            const isWithinOneYear = (dateToCheck && dateToCheck !== "") ? moment(dateToCheck).isBetween(oneYearAgo, today, null, '[]') : true;
            if (!isWithinOneYear) {
                setDateWarning("Your coggins date is more than a year old. Please consider adding an up-to-date coggins.");
            } else {
                setDateWarning("");
            }
        } else {
            setDateWarning("");
        }
    }

    const handleDateChange = (value: (string | null)) => {
        checkIfDateIsWithinOneYearOfToday(value);
        const updatedDataFormState: CogginsDataFormState = {
            ...dataFormState,
            date: value || null
        };
        setDataFormState((prev) => ({
            ...prev,
            date: value || null
        }));
        handleDataFieldChange(updatedDataFormState);
    };

    const handleStateChange = (value: string) => {
        const updatedDataFormState: CogginsDataFormState = {
            ...dataFormState,
            state: value
        };
        setDataFormState((prev) => ({
            ...prev,
            state: value
        }));
        handleDataFieldChange(updatedDataFormState);
    };

    const handleFileChange = async (file: File, userInputTitle?: string) => {
        const updatedDocumentFormState: CogginsDocumentFormState = {
            ...documentFormState,
            fileTitle: userInputTitle,
            fileObject: file
        };
        setDocumentFormState(updatedDocumentFormState);
        handleDocumentFieldChange(updatedDocumentFormState);
        await handleSaveHorseData(selectedHorseState, file, userInputTitle);
    };

    const handleSaveHorseData = async (horseNeedingUpdate?: Horse | null | undefined, currentFile?: (File | null | undefined), userInputTitle?: string) => {
        if (!horseNeedingUpdate) return;

        const currentData = dataRef.current;
        const currentDoc = docRef.current;
        setError("");

        const isSectionValid = isCogginsSectionComplete(currentData, currentDoc, cogginsRequirements);
        if (!isSectionValid) {
            reportValidationState(currentData, currentDoc);
            return;
        }

        if (onSavingStateChange) onSavingStateChange(true);

        let updatedCogginsS3Object: (S3Object | null | undefined) = horseNeedingUpdate.coggins;
        let shouldUpdateHorseInDB = false;
        const updateInput: UpdateHorseInput = {
            id: horseNeedingUpdate.id
        };

        const fileToUseInUpdate = currentFile || currentDoc.fileObject;
        if (fileToUseInUpdate) {
            shouldUpdateHorseInDB = true;
            const fileName = createCogginsDocumentFileName(horseNeedingUpdate, fileToUseInUpdate);
            const uploadResult = await handleUploadS3File(fileName, fileToUseInUpdate);
            if (uploadResult.isSuccess) {
                const key = uploadResult.result;
                const title = "Coggins for " + horseNeedingUpdate.name;
                const description = (userInputTitle || currentDoc.fileTitle || fileToUseInUpdate.name) + " uploaded by " + (user.firstName || "") + " " + (user.lastName || "") + " on " + moment(new Date()).format("MM/DD/YY");
                updatedCogginsS3Object = createS3Object(key, title, fileToUseInUpdate, description);
                updateInput["coggins"] = updatedCogginsS3Object;
            } else {
                setError("Error - could not upload the document. Please try again or contact hello@ringsidepro.com for help.");
            }
        }

        if (currentData.accessionNumber && currentData.accessionNumber !== horseNeedingUpdate.cogginsNumber) {
            shouldUpdateHorseInDB = true;
            updateInput["cogginsNumber"] = currentData.accessionNumber;
        }
        if (currentData.date && currentData.date !== horseNeedingUpdate.cogginsDate) {
            shouldUpdateHorseInDB = true;
            updateInput["cogginsDate"] = currentData.date;
        }
        if (currentData.state && currentData.state !== horseNeedingUpdate.cogginsState) {
            shouldUpdateHorseInDB = true;
            updateInput["cogginsState"] = currentData.state;
        }

        if (shouldUpdateHorseInDB) {
            const updateResult = await updateHorse(updateInput);
            if (updateResult.isSuccess) {
                const updatedHorse: Horse = updateResult.result;
                setSelectedHorseState(updatedHorse);
                if (updatedCogginsS3Object) {
                    const updatedDocumentFormState: CogginsDocumentFormState = {
                        fileTitle: currentDoc.fileTitle,
                        fileObject: null,
                        s3Object: updatedCogginsS3Object,
                    };
                    setDocumentFormState(updatedDocumentFormState);
                    docRef.current = updatedDocumentFormState;
                    reportValidationState(currentData, updatedDocumentFormState);
                } else {
                    reportValidationState(currentData, currentDoc);
                }
            } else {
                setError("Error - the horse could not be updated. Please try again or contact hello@ringsidepro.com for help.");
                reportValidationState(currentData, currentDoc);
            }
        } else {
            reportValidationState(currentData, currentDoc);
        }

        if (onSavingStateChange) onSavingStateChange(false);
    }

    //TODO: after updating a horse, the main SelectHorseSection component will not have the updated Horse
    // Make sure that the Horse dropdown syncs with the updates in this coggins section 
    const handleRemoveDocument = async () => {
        // Upload this to S3 
        setIsLoading(true);
        setError("");
        if (selectedHorseState && selectedHorseState.coggins && selectedHorseState.coggins.key) {
            const removeResult = await handleDeleteS3File(selectedHorseState.coggins?.key);
            if (removeResult.isSuccess) {
                // Update the horse
                let input: UpdateHorseInput = {
                    id: selectedHorseState.id,
                    coggins: null
                };
                const updateResult = await updateHorse(input);
                if (updateResult.isSuccess) {
                    const updatedHorse: Horse = updateResult.result;
                    setSelectedHorseState(updatedHorse);
                    const clearedDocumentFormState: CogginsDocumentFormState = {
                        fileObject: null,
                        s3Object: null,
                    };
                    setDocumentFormState(clearedDocumentFormState);
                    docRef.current = clearedDocumentFormState;
                    reportValidationState(dataRef.current, clearedDocumentFormState);
                    setIsLoading(false);
                } else {
                    setIsLoading(false);
                }
            } else {
                setError("Error - the document could not be removed.");
            }
        }
        setIsLoading(false);
    }

    return (
        <div data-section={"coggins"} ref={cogginsComponentRef}>
            {(!!selectedHorseState) ?
                <>
                    {error && <ErrorAlert error={error} width="12"/> }
                    {dateWarning && <CogginsWarningBanner warning={dateWarning}/> }

                    {/* First Row - Coggins Accession # and Test Date */}
                    <IonRow>
                        {/* Text Input - Coggins Accession # */}
                        <IonCol sizeXs="12" sizeSm="6">
                            <IonItem>
                                <IonLabel position="stacked">Accession #{requiresCogginsData && <RequiredInputIndicator />}</IonLabel>
                                <IonInput 
                                    value={dataFormState.accessionNumber}
                                    onIonChange={(e: CustomEvent) => handleAccessionNumberChange(e.detail.value!)} 
                                />
                            </IonItem>
                        </IonCol>

                        {/* Calendar Date Selection - Test Date */}
                        <IonCol sizeXs="12" sizeSm="4" className="pt-0">
                            <DatePicker 
                                label="Test Date" 
                                isRequired={!!requiresCogginsData} 
                                onChange={(d: (string | null)) => handleDateChange(d)} 
                                value={dataFormState.date || ""} 
                            />
                        </IonCol>

                        {/* Test State Selection */}
                        <IonCol sizeXs="12" sizeSm="2" className="pl-2 pt-1 pb-0">
                            <SelectState 
                                id="state-select" 
                                label="State" 
                                isRequired={requiresCogginsData} 
                                selectedValue={dataFormState.state} 
                                onInputChange={handleStateChange} 
                                isTextDarker={true} 
                            />
                        </IonCol>
                    </IonRow>

                    {/* Second Row - Coggins Test State and File Upload */}
                    {isLoading ? (
                        <CogginsFileUploadSkeleton />
                    ) : (
                        <IonRow>
                            {/* File Upload */}
                            <IonCol size="12" className="pt-0">
                                <CogginsDocumentForm 
                                    horse={selectedHorseState} 
                                    isRequired={requiresCogginsDocument} 
                                    onSubmit={(file: File, title?: string) => handleFileChange(file, title)} 
                                    onDelete={() => handleRemoveDocument()}
                                />
                            </IonCol>
                        </IonRow>
                    )}
                </>
                :
                <IonRow>
                    <IonCol>
                        <p>Select a horse above.</p>
                    </IonCol>
                </IonRow>
            }
        </div>
    );
};

export default CogginsSection;
